import React, { Suspense, useCallback, useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Circle } from 'lucide-react';
import {
  Actions,
  ModelScopeFactory,
  useAction,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import { Text, Tooltip } from '@cotera/client/app/components/ui';
import { ResourceType } from '@cotera/api';
import { FavoriteToggleButton } from '../resources/favorites';
import {
  ResourcePageShell,
  useResourceBreadcrumbs,
} from '../resources/resource-page-shell';
import { useArtifactPanelBackLeading } from '../chat/artifacts/use-artifact-panel-back-leading';
import {
  useActiveArtifactId,
  useIsArtifactFocusView,
} from '@cotera/client/v0/chat/artifact-panel-context';
import { AgentModel, AgentModelProvider, useAgentModel } from './agent.model';
import { AgentStateEditor } from './agent-state-editor';
import { AgentReferenceArtifactsExpander } from './agent-reference-artifacts-expander';
import { ResourceArtifactPanelProvider } from '../chat/artifacts/resource-artifact-panel';
import {
  CommentMarginHost,
  CommentsRailOutlet,
  CommentsRailProvider,
  CommentsRailToggle,
  usePublishCommentTarget,
} from '@cotera/client/app/components/app/rte';
import { registerAgentSeedRuntime } from './seed/runtime';
import { SaveAgentAction } from './save-agent.action';
import { ChangeAgentModelAction } from './change-agent-model.action';
import { CategorizedModelSelector } from './categorized-model-selector';
import { registerArchiveDetailPageBeforeNavigate } from '../resources/actions/archive-resource.action';
import { useBlockNavigationWithModal } from '@cotera/client/app/hooks/use-history-blocker';
import {
  ResourceSelectionModel,
  ResourceType as RSelectionType,
} from '../resources/selection/resource-selection.model';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  RESOURCE_MEASURE,
  useResourcePageLayout,
} from '../resources/resource-page-framing';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import { openArtifactChatSeed } from '../chat-seed';
import { agentChatSeedStrategy } from './seed';
import { useUrlAdapter } from '@cotera/client/app/etc/url-param-watchable';

const UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes. Are you sure you want to leave?';

const AgentPageResourceSelectionSync: React.FC<{
  isNew: boolean;
  skipNextBlockRef: React.MutableRefObject<boolean>;
}> = ({ isNew, skipNextBlockRef }) => {
  const model = useAgentModel();
  const selection = useInScopeModel(ResourceSelectionModel);
  const actionsContext = useActionsContext();
  const title = useWatchableValue(model.titleWatchable);

  useEffect(() => {
    registerArchiveDetailPageBeforeNavigate(() => {
      skipNextBlockRef.current = true;
    });
    return () => {
      registerArchiveDetailPageBeforeNavigate(undefined);
    };
  }, [skipNextBlockRef]);

  useEffect(() => {
    if (selection === null || isNew) {
      return;
    }
    const name =
      title !== null && title.trim() !== '' ? title.trim() : 'Untitled Agent';
    selection.setSelections([
      {
        resourceType: RSelectionType.Agent,
        resourceId: model.agentId,
        name,
      },
    ]);
    actionsContext.targetModel(selection);
  }, [selection, isNew, model.agentId, title, actionsContext]);

  return null;
};

const AgentEditorTabUrlSync: React.FC = () => {
  const model = useAgentModel();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    model.syncEditorTabFromUrl(searchParams);
  }, [model, searchParams]);

  return null;
};

const AgentArtifactContent: React.FC<{
  isNew: boolean;
  skipNextBlockRef?: React.MutableRefObject<boolean>;
}> = ({ isNew, skipNextBlockRef }) => {
  const location = useLocation();
  const locationState = location.state as AgentPageLocationState;
  const model = useAgentModel();
  const layout = useResourcePageLayout(model.mode);
  const org = useWhoami((s) => s.org);
  const title = useWatchableValue(model.titleWatchable);
  const locked = useWatchableValue(model.lockedWatchable);
  const hasUnsavedChanges = useWatchableValue(model.hasUnsavedChangesWatchable);
  const agentModel = useWatchableValue(model.modelWatchable);
  const resourceSnapshot = useWatchableValue(model.resourceWatchable);
  const editorTab = useWatchableValue(model.editorTabWatchable);
  const editorInstance = useWatchableValue(model.editorInstanceWatchable);
  const activeArtifactId = useActiveArtifactId();
  const isFocusView = useIsArtifactFocusView();
  const whoami = useWhoami((x) => x.whoami);

  const commentAuthor = React.useMemo(
    () => ({ id: whoami.id, name: whoami.name ?? whoami.email }),
    [whoami.id, whoami.name, whoami.email]
  );

  // Comments anchor to the prompt's own text, so the Chats tab — which unmounts the
  // editor — has nothing for the rail to point at and withdraws the target, taking
  // the header's toggle with it. See the document page for the rest.
  usePublishCommentTarget({
    source: model.mode === 'page' ? 'page' : 'artifact',
    key: model.agentId,
    active:
      editorTab === 'editor' &&
      (model.mode === 'page' || activeArtifactId === model.agentId),
    editor: editorInstance,
    authorId: whoami.id,
    authorName: whoami.name ?? whoami.email,
  });
  const saveAgentAction = useAction(SaveAgentAction);
  const changeModelAction = useAction(ChangeAgentModelAction);
  const folderIdFromState = locationState?.folderId;
  const folderId = resourceSnapshot?.meta?.folderId ?? null;
  const folderScope = resourceSnapshot?.meta?.folderScope ?? null;

  const shouldBlock = React.useCallback(() => {
    if (skipNextBlockRef?.current === true) {
      skipNextBlockRef.current = false;
      return false;
    }
    return model.hasUnsavedChangesWatchable.snapshot();
  }, [model, skipNextBlockRef]);

  const onSaveAndProceed = React.useCallback(
    async (nextLocation: { pathname: string }) => {
      const isNavigatingToChat = nextLocation.pathname.includes(
        `/${org.slug}/chats/`
      );
      if (!isNavigatingToChat) {
        return false;
      }
      const result = await saveAgentAction.execute({
        silent: true,
        agentId: model.agentId,
        ...(folderIdFromState !== undefined && { folderId: folderIdFromState }),
      });
      if (result.isErr()) {
        return false;
      }
      if (skipNextBlockRef !== undefined) {
        skipNextBlockRef.current = true;
      }
      return true;
    },
    [folderIdFromState, model, org.slug, saveAgentAction, skipNextBlockRef]
  );

  useBlockNavigationWithModal(shouldBlock, {
    message: UNSAVED_CHANGES_MESSAGE,
    onSaveAndProceed,
    blockEnabled: hasUnsavedChanges,
  });

  const breadcrumbs = useResourceBreadcrumbs({
    resourceType: ResourceType.Agent,
    resourceId: model.agentId,
    resourceName: title,
    resourceEmoji: resourceSnapshot?.agent.emoji ?? null,
    folderId: isNew ? null : folderId,
    folderScope: isNew ? null : folderScope,
  });
  const artifactBackLeading = useArtifactPanelBackLeading();

  const agentActions = (
    <>
      {locked && (
        <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20">
          Editing in progress
        </span>
      )}
      <Tooltip
        side="bottom"
        tooltipContent={
          hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'
        }
      >
        <span className="shrink-0">
          {hasUnsavedChanges ? (
            <Circle className="h-2 w-2 fill-amber-500 text-amber-500" />
          ) : (
            <CheckCircle className="h-4 w-4 text-green-600" />
          )}
        </span>
      </Tooltip>
      {!locked && (
        <CategorizedModelSelector
          model={agentModel}
          onModelChange={(m) => void changeModelAction.execute({ model: m })}
        />
      )}
      {/* Last in this agent's own actions, which puts it beside the expand-to-focus
          control the shell appends after them. Those two are both about how you are
          looking at this agent; the model selector is about the agent itself, and
          having comments sit between them grouped it with the wrong thing.

          See the document page for why it goes with the resource's actions at all. */}
      <CommentsRailToggle />
    </>
  );

  useEffect(() => {
    return registerAgentSeedRuntime(model.agentId, {
      editorRef: model.editorRef,
      getPersistSnapshot: () => ({
        editorState: model.getEditorStateForSave(),
        model: model.getModelForSave(),
      }),
      markPersisted: () => {
        model.markAsSaved(model.getEditorStateForSave());
      },
    });
  }, [model]);

  if (resourceSnapshot == null) {
    return <AgentPageSkeleton />;
  }

  const editor = (
    // The same margin the document editor scrolls inside: a strip reserved for the
    // comment cards wherever the pane can spare one, falling back to the count chips
    // where it cannot. It is also this editor's scroll container — `min-h-full` below
    // has always expected an ancestor to be one.
    <CommentMarginHost editor={editorInstance} author={commentAuthor}>
      <AgentStateEditor
        className="min-h-full"
        editorRef={model.editorRef}
        editable={!locked}
      />
    </CommentMarginHost>
  );

  // Only on its own page — as an artifact the agent publishes into the chat's
  // provider instead. See the document page.
  const withCommentsRail = (page: React.ReactElement): React.ReactElement =>
    model.mode === 'page' ? (
      <CommentsRailProvider accepts="page">{page}</CommentsRailProvider>
    ) : (
      page
    );

  return withCommentsRail(
    <ResourcePageShell
      breadcrumbs={breadcrumbs}
      leading={artifactBackLeading}
      // Beside the name — the breadcrumb's last crumb on the page, the artifact
      // switcher in the panel — rather than out in the actions cluster.
      // Nothing to star until the agent has been saved once: an unsaved agent
      // has no server-side id for a favorite to point at.
      titleAdornment={
        isNew ? undefined : (
          <FavoriteToggleButton
            type={ResourceType.Agent}
            id={model.agentId}
            className="h-6 w-6"
            iconClassName="h-3.5 w-3.5"
          />
        )
      }
      actions={agentActions}
      layout={layout}
      surface="editor"
    >
      {model.mode === 'page' ? (
        // Standalone page: clicking a resource mention opens it in a transient
        // side panel, and comments dock outside both as the last column.
        // Embedded-as-artifact (mode==='artifact') renders bare, where the
        // floating thread popover is the whole comments UI.
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden h-full">
          <CommentsRailOutlet>
            <ResourceArtifactPanelProvider>
              {editor}
            </ResourceArtifactPanelProvider>
          </CommentsRailOutlet>
        </div>
      ) : (
        // As an artifact, the rail belongs here only in the focus view — under this
        // agent's own bar, so that bar spans the window. See `DocumentEditLayout`.
        <CommentsRailOutlet enabled={isFocusView}>{editor}</CommentsRailOutlet>
      )}
    </ResourcePageShell>
  );
};

type AgentPageLocationState = { new?: boolean; folderId?: string } | null;

const AgentPageSkeleton: React.FC = () => {
  return (
    <div className="h-full w-full bg-background flex flex-col min-h-0 overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-200/50 shrink-0 flex items-center gap-2">
        <div className="h-6 w-48 rounded bg-coco-accent animate-pulse" />
        <div className="h-4 w-4 rounded bg-coco-accent animate-pulse shrink-0" />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-5">
        <div className={cn('space-y-4', RESOURCE_MEASURE)}>
          <div className="h-10 w-2/3 rounded bg-coco-accent animate-pulse" />
          <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
          <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
          <div className="h-4 w-4/5 rounded bg-coco-accent animate-pulse" />
        </div>
      </div>
    </div>
  );
};

const AgentPageWithModelData: React.FC<{
  isNew: boolean;
  skipNextBlockRef: React.MutableRefObject<boolean>;
}> = ({ isNew, skipNextBlockRef }) => {
  const model = useAgentModel();
  const status = useWatchableValue(model.loadStatusWatchable);
  const resource = useWatchableValue(model.resourceWatchable);

  if (status === 'not-found') {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <Text.P className="text-muted-foreground">Agent not found</Text.P>
      </div>
    );
  }
  if (status === 'loading' || resource == null) {
    return <AgentPageSkeleton />;
  }

  return (
    <AgentArtifactContent isNew={isNew} skipNextBlockRef={skipNextBlockRef} />
  );
};

const AgentPageWithModelScopeResolved: React.FC<{
  id: string;
  isNew: boolean;
  userId: string;
  orgId: string;
  orgSlug: string;
  expandReferences?: boolean;
}> = ({ id, isNew, userId, orgId, orgSlug, expandReferences = false }) => {
  const location = useLocation();
  const locationState = location.state as AgentPageLocationState;
  const folderIdFromState = locationState?.folderId;
  const urlAdapter = useUrlAdapter();
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const watchablesFactory = useWatchablesFactory();
  const navigate = useNavigate();
  const skipNextBlockRef = React.useRef(false);

  const actions = React.useMemo(
    () => [
      new SaveAgentAction(
        client.resource.agent,
        queryClient,
        navigate,
        orgSlug,
        folderIdFromState
      ),
      new ChangeAgentModelAction(),
    ],
    [client.resource.agent, queryClient, navigate, orgSlug, folderIdFromState]
  );

  const createModels = useCallback(
    () => [
      new AgentModel({
        agentId: id,
        orgId,
        userId,
        isNew,
        mode: 'artifact',
        client,
        watchablesFactory,
        urlAdapter,
      }),
      new ResourceSelectionModel(),
    ],
    [id, orgId, userId, isNew, client, watchablesFactory, urlAdapter]
  );

  const activeArtifactId = useActiveArtifactId();
  const target = activeArtifactId === null || activeArtifactId === id;

  return (
    <Actions actions={actions} key={id}>
      <ModelScopeFactory
        createModels={createModels}
        deps={[id, orgId, userId, isNew, urlAdapter]}
      >
        <AgentModelProvider modelId={id} target={target}>
          <AgentPageResourceSelectionSync
            isNew={isNew}
            skipNextBlockRef={skipNextBlockRef}
          />
          <AgentEditorTabUrlSync />
          {expandReferences ? <AgentReferenceArtifactsExpander /> : null}
          <AgentPageWithModelData
            isNew={isNew}
            skipNextBlockRef={skipNextBlockRef}
          />
        </AgentModelProvider>
      </ModelScopeFactory>
    </Actions>
  );
};

const AgentPageWithModelScope: React.FC<{
  id: string;
  isNew: boolean;
  expandReferences?: boolean;
}> = ({ id, isNew, expandReferences = false }) => {
  const userId = useWhoami((s) => s.whoami?.id);
  const orgId = useWhoami((s) => s.org?.id);
  const orgSlug = useWhoami((s) => s.org?.slug);

  if (userId === undefined || orgId === undefined || orgSlug === undefined) {
    return <AgentPageSkeleton />;
  }

  return (
    <AgentPageWithModelScopeResolved
      id={id}
      isNew={isNew}
      userId={userId}
      orgId={orgId}
      orgSlug={orgSlug}
      expandReferences={expandReferences}
    />
  );
};

export const AgentPageWithFetchedData: React.FC<{
  id: string;
  /** Surface this agent's @-mentioned documents/agents as sibling artifact tabs. */
  expandReferences?: boolean;
}> = ({ id, expandReferences = false }) => {
  return (
    <AgentPageWithModelScope
      id={id}
      isNew={false}
      expandReferences={expandReferences}
    />
  );
};

export const AgentArtifactPage: React.FC<{
  id: string;
  isNew?: boolean;
  /** Surface this agent's @-mentioned documents/agents as sibling artifact tabs. */
  expandReferences?: boolean;
}> = ({ id, isNew = false, expandReferences = false }) => {
  return (
    <Suspense fallback={<AgentPageSkeleton />}>
      <AgentPageWithModelScope
        id={id}
        isNew={isNew}
        expandReferences={expandReferences}
      />
    </Suspense>
  );
};

/**
 * Legacy `/agent/:id` route — redirects into the chat seed flow so agent editing
 * happens in the chat artifact panel instead of a standalone page.
 */
export const AgentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const client = useTenantedClient();
  const orgSlug = useWhoami((s) => s.org?.slug);
  const locationState = location.state as AgentPageLocationState;

  useTracking(
    CocoEvent.AgentViewed,
    { agentId: id ?? null },
    { enabled: id !== undefined }
  );

  useEffect(() => {
    if (id === undefined || orgSlug === undefined) {
      return;
    }
    const abortController = new AbortController();
    void openArtifactChatSeed({
      client: client.resource,
      navigate,
      orgSlug,
      strategy: agentChatSeedStrategy,
      artifactId: id,
      isNew: locationState?.new === true,
      folderId: locationState?.folderId,
      replace: true,
      abortSignal: abortController.signal,
    });
    return () => {
      abortController.abort();
    };
  }, [
    id,
    navigate,
    orgSlug,
    client.resource,
    locationState?.new,
    locationState?.folderId,
  ]);

  if (id === undefined) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <Text.P className="text-muted-foreground">Invalid agent</Text.P>
      </div>
    );
  }

  return <AgentPageSkeleton />;
};
